// Place Order related store state and actions.

import moment from 'moment'
import axiosApiClient from '@/api/axiosApiClient'
import log from '@/utils/log'

const { VUE_APP_ENABLE_PLACE_ORDER_LOCATION } = process.env

const DEFAULT_FORMAT_ID = 'COMMON_PLACE_ORDER' // TODO: confirm default format ID

// Utility functions:
// Gets tranlation value of given locale from translations array.
const getTranslationValue = (translations, locale, fallback, key = 'value') => {
	const found = translations.find(
		translation => translation.locale === locale
	)
	// use fallback if translation not found
	return found ? found[key] : fallback
}

const readFileAsDataURL = file =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.readAsDataURL(file)
		reader.onload = () => resolve(reader.result)
		reader.onerror = error => reject(error)
	})

const parseRequests = requests => {
	return requests.map(item => {
		let pickupContacts = []
		if (item.address[0].contacts) {
			pickupContacts = item.address[0].contacts
		} else {
			pickupContacts = item.contacts
		}
		let dropoffContacts = []
		if (item.address[1] && item.address[1].contacts) {
			dropoffContacts = item.address[1].contacts
		}
		return {
			clientId: item.clientId,
			clientRefs: item.clientRefs,
			locationId: item.locationId,
			deliveryStage: item.deliveryStage,
			pickupAddress: item.address[0].text,
			pickupContacts: pickupContacts,
			pickupAt: item.pickupAt,
			dropoffAddress: item.address[1] ? item.address[1].text : 'N/A',
			dropoffContacts: dropoffContacts,
			deliveryBy: item.deliveryBy,
			parcelNum: item.parcelNum,
			totalWeight: item.totalWeight || 'N/A',
			notes: item.notes,
			tags: item.tags
		}
	})
}

// State
export const state = {
	uiState: 'DEFAULT', // DEFAULT, VALIDATE_SUCCESS, VALIDATE_ERROR, SUBMIT_SUCCESS, SUBMIT_ERROR, ...
	loading: false, // loading state
	error: null, // generic errors
	templateUrl: '', // sample CSV template URL fetched from backend
	file: null, // uploaded file object
	fileUrl: '', // uploaded file URL
	spreadsheetData: [], // from API response
	spreadsheetInfo: null, // from API response
	spreadsheetError: null, // from API response

	formats: [], // spreadsheet formats
	formatId: DEFAULT_FORMAT_ID, // the spreadsheet format ID to use

	deliveryStage: 'LAST_MILE', // default LAST_MILE
	deliveryStages: ['LAST_MILE', 'FIRST_MILE', 'INTERIM'], // delivery stage selection

	clientId: null,
	locationId: null
}

// Getters
export const getters = {}

// Mutations
export const mutations = {
	RESET(state) {
		state.uiState = 'DEFAULT'
		state.loading = false
		state.error = null
		state.file = null
		state.fileUrl = ''
		state.spreadsheetData = []
		state.spreadsheetInfo = null
		state.spreadsheetError = null
	},
	CLEAR(state) {
		state.formats = []
		state.formatId = DEFAULT_FORMAT_ID
		state.deliveryStage = 'LAST_MILE'
		state.clientId = null
		state.locationId = null
	},
	SET_TEMPLATE_URL(state, url) {
		state.templateUrl = url
	},
	VALIDATION_REQUEST(state, file) {
		state.file = file
		state.loading = true
		state.error = null
		state.spreadsheetData = []
		state.spreadsheetInfo = null
		state.spreadsheetError = null
	},
	VALIDATION_SUCCESS(state, response) {
		state.uiState = 'VALIDATE_SUCCESS'
		state.loading = false
		state.error = null
		state.spreadsheetData = parseRequests(response.meta.requests)
		state.spreadsheetInfo = createInfo(response.meta.requests)
		state.spreadsheetError = null
	},
	VALIDATION_FAILURE(state, error) {
		state.uiState = 'VALIDATE_ERROR'
		state.loading = false
		state.error = error.error
		state.spreadsheetData = []
		state.spreadsheetInfo = null
		state.spreadsheetError = {
			feedbackCsv: error.errorFeedbackCsv,
			rows: error.errorRows
		}
	},
	SUBMISSION_REQUEST(state) {
		state.loading = true
		state.error = null
	},
	SUBMISSION_SUCCESS(state, response) {
		state.uiState = 'SUBMIT_SUCCESS'
		state.loading = false
		state.error = null
		state.fileUrl = response.fileUrl
		state.spreadsheetData = []
		state.spreadsheetInfo = null
		state.spreadsheetError = null
	},
	SUBMISSION_FAILURE(state, error) {
		state.uiState = 'SUBMIT_ERROR'
		state.loading = false
		state.error = error.error
	},
	SET_FORMATS(state, formats) {
		state.formats = formats
	},
	SET_FORMAT_ID(state, formatId) {
		state.formatId = formatId
	},
	SET_DELIVERY_STAGE(state, deliveryStage) {
		state.deliveryStage = deliveryStage
	},
	SET_CLIENT(state, clientId) {
		state.clientId = clientId
	},
	SET_LOCATION(state, locationId) {
		state.locationId = locationId
	}
}

// Actions
export const actions = {
	// Fetches spreadsheet format (to get template URL).
	// async fetchSpreadsheetFormat({ commit, rootState, state }) {
	// 	try {
	// 		const { data } = await axiosApiClient.get(`order-spreadsheet-formats/${state.formatId}`)
	// 		const { csvs } = data
	// 		if (csvs && csvs.length) {
	// 			const { locale } = rootState.config // get current locale from config store
	// 			const url = getTranslationValue(csvs, locale, '', 'url')
	// 			commit('SET_TEMPLATE_URL', url)
	// 		}
	// 	}
	// 	catch (error) {
	// 		console.error('fetchSpreadsheetFormat', error)
	// 	}
	// },

	// Validates/submits spreadsheet file.
	async uploadSpreadsheet(
		{ commit, dispatch, rootState, state },
		{ submit, file } = {}
	) {
		const actionType = submit ? 'SUBMISSION' : 'VALIDATION'
		if (submit) {
			commit('SUBMISSION_REQUEST', state)
		} else {
			commit('VALIDATION_REQUEST', file)
			// this is unreliable in Windows:
			// if (file.type !== 'text/csv' || file.type !== 'application/vnd.ms-excel') {
			// 	commit('VALIDATION_FAILURE', { error: i18next.t('PORTAL.Place Order.msg_file_type_error') })
			// 	return
			// }
		}

		const csvFile = submit ? state.file : file
		let csvText = ''
		try {
			csvText = await readFileAsDataURL(csvFile)
			csvText = csvText.replace(/^data:.+;base64,/, '')
		} catch (error) {
			log.error('Failed to read CSV file', null, error.stack)
			commit(`${actionType}_FAILURE`, { error })
			return
		}

		try {
			const payload = {
				clientId: state.clientId || rootState.auth.currentUser.clientId, // get clientId from auth store
				...(VUE_APP_ENABLE_PLACE_ORDER_LOCATION === 'true' && {
					locationId: state.locationId
				}),
				fileName: csvFile.name,
				deliveryStage: state.deliveryStage,
				orderSpreadsheetFormatId: state.formatId,
				spreadsheet: csvText,
				previewOnly: !submit,
				requestEmail: rootState.auth.currentUser.email
			}
			const { data } = await axiosApiClient.post(
				'/order-imports',
				payload
			)
			const { errorFeedbackCsv, errorRows } = data.meta
			if (errorFeedbackCsv) {
				commit(`${actionType}_FAILURE`, { errorFeedbackCsv, errorRows })
			} else {
				commit(`${actionType}_SUCCESS`, data)
				if (submit) {
					dispatch(
						'spreadsheetSubmissions/fetchSpreadsheetSubmissions',
						undefined,
						{ root: true }
					)
				}
			}
		} catch (error) {
			let errorFeedbackCsv = null
			let errorRows = null
			const { response } = error
			if (response && response.data && response.data.meta) {
				errorFeedbackCsv = response.data.meta.errorFeedbackCsv
				errorRows = response.data.meta.errorRows
			}
			commit(`${actionType}_FAILURE`, {
				error,
				errorFeedbackCsv,
				errorRows
			})
			log.error(
				'Failed to upload spreadsheet',
				{ category: 'API' },
				error.stack
			)
		}
	},

	// Resets store state.
	reset({ commit }) {
		commit('RESET')
	},

	// Clear everything.
	clear({ commit }) {
		commit('RESET')
		commit('CLEAR')
	},

	// Fetches ALL clients' spreadsheet formats.
	async fetchAllFormats({ commit, dispatch, rootState }) {
		try {
			const { data } = await axiosApiClient.get(
				`order-spreadsheet-formats`
			)
			const { data: dataCommon } = await axiosApiClient.get(
				`order-spreadsheet-formats?id=COMMON_*`
			)
			let allFormats = []
			if (dataCommon && dataCommon.length)
				allFormats = allFormats.concat(dataCommon)
			if (data && data.length) allFormats = allFormats.concat(data)
			const list = []
			const ids = []
			allFormats.forEach(i => {
				if (!ids.includes(i.id)) {
					list.push(i)
					ids.push(i.id)
				}
			})
			if (
				list.length > 1 ||
				(list.length === 1 && list[0].id !== DEFAULT_FORMAT_ID)
			) {
				commit('SET_FORMATS', list)
				dispatch('changeFormat', { formatId: list[0].id })
			} else if (list.length) {
				const { csvs } = list[0]
				if (csvs && csvs.length) {
					const { locale } = rootState.config // get current locale from config store
					const url = getTranslationValue(csvs, locale, '', 'url')
					commit('SET_TEMPLATE_URL', url)
				} else {
					commit('SET_TEMPLATE_URL', '')
				}
			}
		} catch (error) {
			log.error(
				'Failed to fetch spreadsheet formats',
				{ category: 'API' },
				error.stack
			)
		}
	},

	// Sets spreadsheet format ID to use.
	changeFormat({ commit, rootState, state }, { formatId }) {
		commit('SET_FORMAT_ID', formatId)
		const { csvs } = state.formats.find(f => f.id === formatId)
		if (csvs && csvs.length) {
			const { locale } = rootState.config // get current locale from config store
			const url = getTranslationValue(csvs, locale, '', 'url')
			commit('SET_TEMPLATE_URL', url)
		} else {
			commit('SET_TEMPLATE_URL', '')
		}
	},

	// Sets delivery stage to use.
	changeDeliveryStage({ commit }, { deliveryStage }) {
		commit('SET_DELIVERY_STAGE', deliveryStage)
	},

	changeClient({ commit, rootState }, { clientId }) {
		commit('SET_CLIENT', clientId)
		commit(
			'SET_LOCATION',
			rootState.config.clients.find(c => c.id === clientId).locations[0]
		)
	},

	changeLocation({ commit }, { locationId }) {
		commit('SET_LOCATION', locationId)
	}
}

function createInfo(list) {
	// group item by date and count (for info display)
	const group = list.reduce((acc, { pickupAt }) => {
		const key = moment(pickupAt).format('YYYY-MM-DD')
		if (key in acc) acc[key]++
		else acc[key] = 1
		return acc
	}, {})
	return {
		rowsProcessed: list.length,
		group
	}
}
